import { memo, useMemo, useState } from "react";
import type { FallbackTier } from "@kmitl/shared";
import { safeUrl } from "../lib/safeUrl";
import { PanelHeader } from "./PanelHeader";

interface TrendPoint { time: string; value: number }
interface RelatedQuery { query: string; value: number; link?: string | null }

export interface TrendsSnapshot {
  lang: "en" | "th" | "zh-CN";
  keyword: string;
  geo: string;
  interestOverTime: TrendPoint[];
  relatedTop: RelatedQuery[];
  relatedRising: RelatedQuery[];
  fetchedAt: string;
  err?: string;
}

interface Props {
  snapshots: TrendsSnapshot[];
  loading: boolean;
  ageMinutes: number;
  onRefresh: () => void;
  /** Surfaced when the upstream is blocked (Google rate-limits Cloudflare IPs). */
  tier?: FallbackTier | "loading";
}

const LANGS: Array<{ id: TrendsSnapshot["lang"]; label: string }> = [
  { id: "en",    label: "EN" },
  { id: "th",    label: "TH" },
  { id: "zh-CN", label: "CN" },
];

function trendErrorMessage(err: string | undefined): string {
  if (!err) return "No interest data yet. Refresh to retry.";
  if (/Unexpected token|not valid JSON|<HEAD|<HTML|DOCTYPE/i.test(err)) {
    return "Google Trends returned a non-data response. Showing the dashboard without search telemetry.";
  }
  return `${err}. Showing the dashboard without search telemetry.`;
}

/**
 * Trends panel — top of the right rail. Shows what people are searching for
 * around the active "KMITL" keyword in each language, with the past-90-day
 * interest curve as a tiny sparkline.
 */
export const TrendsPanel = memo(function TrendsPanel({ snapshots, loading, ageMinutes, onRefresh, tier }: Props) {
  const [lang, setLang] = useState<TrendsSnapshot["lang"]>("en");
  const active = useMemo(
    () => snapshots.find((s) => s.lang === lang) ?? snapshots[0] ?? null,
    [snapshots, lang],
  );

  const sparkline = useMemo(() => {
    if (!active?.interestOverTime?.length) return null;
    const values = active.interestOverTime.map((p) => p.value);
    const maxV = Math.max(...values, 1);
    const w = 280;
    const h = 36;
    const denom = Math.max(1, values.length - 1);
    const pts = values
      .map((v, i) => {
        const x = (i / denom) * w;
        const y = h - (v / maxV) * h;
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(" ");
    const peak = Math.max(...values);
    const last = values[values.length - 1];
    return { pts, w, h, peak, last };
  }, [active]);

  const tierForHeader: FallbackTier | undefined =
    tier === "loading" || tier === undefined ? undefined : tier;

  return (
    <section className="trends-panel">
      <PanelHeader
        title="WIKI VIEWS · 90 D"
        ageMinutes={ageMinutes}
        fallbackTier={tierForHeader}
        source="wikipedia"
        actions={
          <>
            {active && <span className="trends-keyword">{active.keyword}</span>}
            <button
              type="button"
              className="trends-refresh mono"
              onClick={onRefresh}
              disabled={loading}
              aria-label="Refresh trends"
              title={`Refreshed ${ageMinutes}m ago — click to refresh`}
            >
              {loading ? "…" : "↻"}
            </button>
          </>
        }
      />

      <div className="trends-lang">
        {LANGS.map((l) => (
          <button
            key={l.id}
            onClick={() => setLang(l.id)}
            className={`trends-lang-btn mono ${lang === l.id ? "active" : ""}`}
            aria-pressed={lang === l.id}
          >
            {l.label}
          </button>
        ))}
      </div>

      {active ? (
        <>
          {sparkline ? (
            <div className="trends-spark">
              <svg viewBox={`0 0 ${sparkline.w} ${sparkline.h}`} aria-hidden="true">
                <polyline
                  points={sparkline.pts}
                  stroke="var(--accent)"
                  strokeWidth="1.5"
                  fill="none"
                />
              </svg>
              <div className="trends-spark-foot mono">
                <span>peak {sparkline.peak}</span>
                <span>now {sparkline.last}</span>
              </div>
            </div>
          ) : (
            <div className="trends-empty">
              {trendErrorMessage(active.err)}
            </div>
          )}

          {active.relatedTop.length > 0 && (
            <div>
              <div className="eyebrow mono trends-section-label">Top related</div>
              <ul className="trends-related">
                {active.relatedTop.slice(0, 6).map((q, i) => (
                  <li key={`top-${q.query}-${i}`}>
                    <a
                      href={safeUrl(q.link) ?? `https://trends.google.com/trends/explore?q=${encodeURIComponent(q.query)}`}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      <span className="trends-q">{q.query}</span>
                      <span className="trends-v mono">{q.value}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {active.relatedRising.length > 0 && (
            <div>
              <div className="eyebrow mono trends-section-label">Rising ↑</div>
              <ul className="trends-related">
                {active.relatedRising.slice(0, 6).map((q, i) => (
                  <li key={`rise-${q.query}-${i}`}>
                    <a
                      href={safeUrl(q.link) ?? `https://trends.google.com/trends/explore?q=${encodeURIComponent(q.query)}`}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      <span className="trends-q">{q.query}</span>
                      <span className="trends-v mono trends-v-rise">
                        {q.value >= 5000 ? "Breakout" : `+${q.value}%`}
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      ) : (
        <div className="trends-empty">
          {tier === "unavailable"
            ? "Wikipedia pageviews unavailable."
            : "Loading Wikipedia pageviews…"}
        </div>
      )}
    </section>
  );
});
